import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';

interface Option {
    value: string;
    label: string;
}

interface PriorityOption extends Option {
    color?: string;
    icon?: string;
}

/**
 * Translated company type options for select/combobox inputs.
 *
 * Lives in a hook (not SettingsContext) because SettingsProvider is mounted
 * outside the Inertia tree, where `usePage()` — and therefore translations —
 * is unavailable. Forms call this within the Inertia page context.
 *
 * Resolution (including the lowercased-key fallback that lets the capitalized
 * stored values such as "Client" reach their lowercase lang keys) lives in
 * `settingLabel`.
 */
export function useCompanyTypeOptions(): Option[] {
    const { settings } = useSettings();
    const { t } = useTranslations();

    return settings.companyTypes.map((type) => ({
        value: type,
        label: settingLabel(t, 'company_types', type),
    }));
}

/**
 * Translated company category options for select/multi-select inputs.
 */
export function useCompanyCategoryOptions(): Option[] {
    const { settings } = useSettings();
    const { t } = useTranslations();

    return settings.companyCategories.map((category) => ({
        value: category,
        label: settingLabel(t, 'company_categories', category),
    }));
}

/**
 * Translated company priority options.
 *
 * Resolves on the stored `value` slug, never the `label` held in the settings
 * JSON — that label is authored in English and would otherwise win over the
 * admin's per-language override.
 */
export function useCompanyPriorityOptions(): PriorityOption[] {
    const { settings } = useSettings();
    const { t } = useTranslations();

    return (settings.companyPriorities ?? []).map((priority) => ({
        value: priority.value,
        label: settingLabel(t, 'company_priorities', priority.value),
        color: priority.color,
        icon: priority.icon,
    }));
}
